// ============================================================================
// quote-revision.ts — Deterministic Quote Revision Derivation & Formatting
// ----------------------------------------------------------------------------
// Manages quote revision numbering and identifiers when an existing quoted project
// is re-imported or re-queued. Revisions process from scratch while preserving the
// original baseline quote (Rev 0) intact for audit.
//
// Formats:
//   - Quote number suffix: "-R1", "-R2", etc. (e.g. "QR592460-R1")
//   - Client-facing / Display badge: "Rev 1", "Rev 2"
//   - Title decoration: " (Rev 1)"
// ============================================================================

export interface RevisionParseResult {
    base: string;
    revision: number | null;
    suffix: string | null;
}

const REVISION_SUFFIX_REGEX = /[-_ ]+R(\d+)$/i;
const REVISION_PAREN_REGEX = /\s*\((?:Rev(?:ision)?\s*)(\d+)\)$/i;
const REVISION_WORD_REGEX = /\s*[-_ ]+Rev(?:ision)?\s*(\d+)$/i;

/**
 * Parses any revision indicator from a quote number or string.
 */
export function parseRevisionSuffix(raw?: string | null): RevisionParseResult {
    if (!raw) return { base: '', revision: null, suffix: null };
    const text = String(raw).trim();
    if (!text) return { base: '', revision: null, suffix: null };

    // Check for "-R1", "-R2" suffix
    const matchSuffix = text.match(REVISION_SUFFIX_REGEX);
    if (matchSuffix && matchSuffix[1]) {
        const rev = parseInt(matchSuffix[1], 10);
        const base = text.slice(0, matchSuffix.index).trim();
        return { base, revision: rev, suffix: `-R${rev}` };
    }

    // Check for " (Rev 1)" parenthetical
    const matchParen = text.match(REVISION_PAREN_REGEX);
    if (matchParen && matchParen[1]) {
        const rev = parseInt(matchParen[1], 10);
        const base = text.slice(0, matchParen.index).trim();
        return { base, revision: rev, suffix: `-R${rev}` };
    }

    // Check for " - Rev 1" word suffix
    const matchWord = text.match(REVISION_WORD_REGEX);
    if (matchWord && matchWord[1]) {
        const rev = parseInt(matchWord[1], 10);
        const base = text.slice(0, matchWord.index).trim();
        return { base, revision: rev, suffix: `-R${rev}` };
    }

    return { base: text, revision: null, suffix: null };
}

/**
 * Returns clean base quote number without any revision suffix.
 */
export function stripRevisionSuffix(raw?: string | null): string {
    return parseRevisionSuffix(raw).base;
}

/**
 * Derives the next integer revision number (e.g. 1 for first revision, 2 for second)
 * based on the existing project record and any sibling revisions in the project set.
 */
export function deriveNextRevisionNumber(existingProject?: any, allProjects?: any[]): number {
    let highestRevision = 0;

    const inspectProject = (p: any) => {
        if (!p || typeof p !== 'object') return;

        // Check explicit revisionNumber field
        const num = Number(p.revisionNumber ?? p.revision_number ?? p.projectFolder?.revisionNumber ?? p.project_folder?.revisionNumber);
        if (Number.isFinite(num) && num > highestRevision) {
            highestRevision = num;
        }

        // Check quoteNumber
        const qn = p.quoteNumber ?? p.quote_number ?? p.orderNumber ?? p.order_number ?? p.formData?.orderNumber;
        const parsedQn = parseRevisionSuffix(qn);
        if (parsedQn.revision && parsedQn.revision > highestRevision) {
            highestRevision = parsedQn.revision;
        }

        // Check title
        const parsedTitle = parseRevisionSuffix(p.title);
        if (parsedTitle.revision && parsedTitle.revision > highestRevision) {
            highestRevision = parsedTitle.revision;
        }
    };

    inspectProject(existingProject);

    if (Array.isArray(allProjects) && existingProject) {
        const rootId = String(
            existingProject.restartOfProjectId
            ?? existingProject.restart_of_project_id
            ?? existingProject.projectFolder?.restartOfProjectId
            ?? existingProject.id
            ?? '',
        ).trim().toLowerCase();

        for (const candidate of allProjects) {
            if (!candidate || typeof candidate !== 'object') continue;
            const candidateId = String(candidate.id || '').trim().toLowerCase();
            const candidateRoot = String(
                candidate.restartOfProjectId
                ?? candidate.restart_of_project_id
                ?? candidate.projectFolder?.restartOfProjectId
                ?? candidateId,
            ).trim().toLowerCase();

            if (rootId && (candidateRoot === rootId || candidateId === rootId)) {
                inspectProject(candidate);
            }
        }
    }

    return highestRevision + 1;
}

/**
 * Formats a quote number with the given revision suffix (e.g. "QR592460" -> "QR592460-R1").
 */
export function formatQuoteNumberWithRevision(rawQuoteNumber?: string | null, revisionNumber: number = 1): string {
    const raw = String(rawQuoteNumber || '').trim();
    if (!raw) return `-R${revisionNumber}`;
    const base = stripRevisionSuffix(raw);
    return `${base}-R${revisionNumber}`;
}

/**
 * Formats a project title with the revision indicator.
 */
export function formatTitleWithRevision(rawTitle?: string | null, revisionNumber: number = 1): string {
    const raw = String(rawTitle || '').trim();
    if (!raw) return `Project (Rev ${revisionNumber})`;
    const base = stripRevisionSuffix(raw);
    return `${base} (Rev ${revisionNumber})`;
}

/**
 * Detects whether a project record represents a revision (Rev >= 1).
 */
export function isRevisionProject(project?: any): boolean {
    if (!project || typeof project !== 'object') return false;

    const num = Number(project.revisionNumber ?? project.revision_number ?? project.projectFolder?.revisionNumber ?? project.project_folder?.revisionNumber);
    if (Number.isFinite(num) && num > 0) return true;

    if (project.intakeAttemptKind === 'revision' || project.projectFolder?.intakeAttemptKind === 'revision') return true;

    const qn = project.quoteNumber ?? project.quote_number ?? project.orderNumber ?? project.order_number ?? project.formData?.orderNumber;
    if (parseRevisionSuffix(qn).revision !== null) return true;

    if (parseRevisionSuffix(project.title).revision !== null) return true;

    if (project.restartOfProjectId || project.restart_of_project_id || project.projectFolder?.restartOfProjectId) {
        if (project.intakeAttemptKind !== 'restart-from-source' && project.projectFolder?.intakeAttemptKind !== 'restart-from-source') {
            return true;
        }
    }

    return false;
}

/**
 * Returns the revision badge label (e.g. "Rev 1", "Rev 2") or null if unversioned baseline.
 */
export function getRevisionBadgeText(project?: any): string | null {
    if (!project || typeof project !== 'object') return null;

    const num = Number(project.revisionNumber ?? project.revision_number ?? project.projectFolder?.revisionNumber ?? project.project_folder?.revisionNumber);
    if (Number.isFinite(num) && num > 0) return `Rev ${num}`;

    const qn = project.quoteNumber ?? project.quote_number ?? project.orderNumber ?? project.order_number ?? project.formData?.orderNumber;
    const parsedQn = parseRevisionSuffix(qn);
    if (parsedQn.revision && parsedQn.revision > 0) return `Rev ${parsedQn.revision}`;

    const parsedTitle = parseRevisionSuffix(project.title);
    if (parsedTitle.revision && parsedTitle.revision > 0) return `Rev ${parsedTitle.revision}`;

    if (project.intakeAttemptKind === 'revision' || project.projectFolder?.intakeAttemptKind === 'revision') {
        return 'Rev 1';
    }

    if (project.restartOfProjectId || project.restart_of_project_id || project.projectFolder?.restartOfProjectId) {
        if (project.intakeAttemptKind !== 'restart-from-source' && project.projectFolder?.intakeAttemptKind !== 'restart-from-source') {
            return 'Rev 1';
        }
    }

    return null;
}
